import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomButton,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomMoneyInput,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';

import {
  instructionDetailPriceUpdate,
  setTotalAutoLoading,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
  setAutoLoadingNotIncludedCards,
  getTotalAutoLoadingParams, getDetailsPageList,
} from '@src/store/slice/autoUploadInstructionsSlice';

import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { isGreaterMaximumLoadedAmount } from '@src/utils/utils';

import { Form } from 'antd';

import '@src/styles/autoUploadInstruction/monthlyOrder.scss';

const MonthlyOrder = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 1199.98px)' });

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const {
    selectedAutoUploadDetails,
    totalAutoLoading,
    isAllSelectedRowKeys,
    integrationId,
    autoLoadingId,
    autoLoadingNotIncludedCards,
    detailTableProperty,
    autoUploadFilterItems,
  } = useSelector((state) => state?.autoUploadInstructions);
  const { maximumLoadedAmount } = useSelector((state) => state?.orderLimits);

  const maximumLoadingLimitMessage = useText('maximumLoadingLimitPerCardIs');

  const [monthlyPrice, setMonthlyPrice] = useState(totalAutoLoading.monthlyPrice);

  const getAddressIdList = (autoUploadFilterItems) => {
    return Array.isArray(autoUploadFilterItems)
      ? autoUploadFilterItems.map(item => {
        if (Array.isArray(item)) {
          return item.length > 0 ? { addressId: item[0] } : null;
        } else if (item && typeof item === 'object' && item.addressId) {
          return { addressId: item.addressId };
        }
        return null;
      }).filter(item => item !== null)
      : null;
  }

  useEffect(() => {
    if (totalAutoLoading) {
      form.setFieldsValue({
        monthlyPrice: monthlyPrice || totalAutoLoading.monthlyPrice,
      });
    }
  }, [totalAutoLoading, form]);

  const standartUpdate = async (periodType) => {

    const addressIdList = getAddressIdList(autoUploadFilterItems);

    if (autoLoadingId && autoLoadingId > 0) {
      await dispatch(
        getDetailsPageList({
          page: detailTableProperty?.currentPage,
          pageSize: detailTableProperty?.pageSize,
          showOnlyPriced: false,
          autoLoadingId: totalAutoLoading?.id,
          addressIdList: addressIdList,
        }),
      );
      const body = {
        autoLoadingId: autoLoadingId,
        integrationId: 0,
      };
      await dispatch(getTotalAutoLoadingParams(body));
    }
  };

  const submitAll = async () => {
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.monthlyPrice,
        })
      ) {
        const msg = maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount);
        errorDialog({
          title: <Text t="error" />,
          message: msg,
        });
        return;
      }
      const justCardNumberFilterItems = Array.isArray(autoUploadFilterItems)
        ? autoUploadFilterItems.map(item => item.cardNumber)
        : [];

      const notSelectedCards = Array.isArray(autoLoadingNotIncludedCards)
        ? autoLoadingNotIncludedCards.map(item => item.cardNumber)
        : [];


      let data = {
        dayCount: null,
        price: values.monthlyPrice,
        autoLoadingId: totalAutoLoading?.id,
        cardNumbers: autoUploadFilterItems?.length === 0 ? null : justCardNumberFilterItems,
        notIncludedCards: (notSelectedCards?.length === 0 || !isAllSelectedRowKeys) ? null : notSelectedCards,
      };

      const res = await dispatch(instructionDetailPriceUpdate(data));

      if (instructionDetailPriceUpdate.fulfilled.match(res)) {
        await dispatch(
          setTotalAutoLoading({
            ...totalAutoLoading,
            periodType: 1,
          }),
        );

        await standartUpdate(1);

        successDialog({
          title: <Text t="success" />,
          message: res?.payload.message || <Text t="successfullySent" />,
          onOk: async () => {
            await dispatch(setSelectedRowKeys([]));
            await dispatch(setIsAllSelectedRowKeys(false));
            await dispatch(setAutoLoadingNotIncludedCards([]))
          },
        });
      }
    } catch (e) { }
  };

  const submitSelected = async () => {
    if (isAllSelectedRowKeys) {
      await submitAll();
      return;
    }
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.monthlyPrice,
        })
      ) {
        const msg = maximumLoadingLimitMessage.replaceAll('{amount}',  maximumLoadedAmount);
        errorDialog({
          title: <Text t="error" />,
          message: msg,
        });
        return;
      }

      if (selectedAutoUploadDetails.length > 0) {
        let data = {
          price: values.monthlyPrice,
          cardNumbers: selectedAutoUploadDetails.map((item) => item.cardNumber),
          autoLoadingId: totalAutoLoading?.id,
        };

        const res = await dispatch(instructionDetailPriceUpdate(data));

        if (instructionDetailPriceUpdate.fulfilled.match(res)) {
          await dispatch(
            setTotalAutoLoading({
              ...totalAutoLoading,
              periodType: 1,
            }),
          );

          await standartUpdate(1);

          successDialog({
            title: <Text t="success" />,
            message: res?.payload.message || <Text t="successfullySent" />,
            onOk: async () => {
              await dispatch(setSelectedRowKeys([]));
              await dispatch(setIsAllSelectedRowKeys(false));
            },
          });
        }
      } else {
        errorDialog({
          title: <Text t="error" />,
          htmlContent: <Text t="noSelectedRecord" />,
        });
      }
    } catch (e) { }
  };

  const handleMonthlyPrice = async (val) => {
    setMonthlyPrice(val);
    dispatch(setTotalAutoLoading({ ...totalAutoLoading, monthlyPrice: val }));
  };

  return (
    <CustomCollapseCard
      className="auto-upload-instruction-monthly-order-card"
      cardTitle={<Text t="orderAmount2" />}
    >
      <div className="monthly-order-container">
        <CustomForm
          name="monthlyOrderForm"
          className="monthly-order-form"
          initialValues={{}}
          autoComplete="off"
          layout={'horizontal'}
          form={form}
        >
          <div className="form-item">
            <div className="monthly-amount-content">
              <CustomFormItem
                label={<Text t="howMuchDoYouWantToLoadPer" />}
                name="monthlyPrice"
                className="monthly-amount-form-item"
                rules={[{ required: true, message: <Text t="requiredField2" /> }]}
              >
                <CustomMoneyInput
                  id="ticketRestaurant-autoUploadInstructions-instructionInformations-montlyOrder-monthlyPrice-moneyInput"
                  autoComplete="off"
                  height={36}
                  value={monthlyPrice}
                  onChange={handleMonthlyPrice}
                />
                <span className="info-text"><Text t="tlMonthVatIncluded" /></span>
              </CustomFormItem>
            </div>
            <CustomFormItem label={false} className=" ">
              {!isMobil && (
                <div className="action-buttons">
                  <CustomButton
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-montlyOrder-cancel-btn"
                    className="cancel-btn"
                    onClick={submitSelected}
                  >
                    <Text t="applyToSelected2" />
                  </CustomButton>
                  <CustomButton
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-montlyOrder-primary-btn"
                    className="primary-btn"
                    onClick={submitAll}
                  >
                    <Text t="applyToAll2" />
                  </CustomButton>
                </div>
              )}
            </CustomFormItem>
          </div>
        </CustomForm>

        {isMobil && (
          <div className="action-buttons">
            <CustomButton
              id="ticketRestaurant-autoUploadInstructions-instructionInformations-montlyOrder-second-cancel-btn"
              className="cancel-btn"
              onClick={submitSelected}
            >
              <Text t="applyToSelected2" />
            </CustomButton>
            <CustomButton
              id="ticketRestaurant-autoUploadInstructions-instructionInformations-montlyOrder-second-primary-btn"
              className="primary-btn"
              onClick={submitAll}
            >
              <Text t="applyToAll2" />
            </CustomButton>
          </div>
        )}
      </div>
    </CustomCollapseCard>
  );
};

export default MonthlyOrder;
